/**
 * KITE drag-and-drop Page Builder — document model + API contracts.
 *
 * A page is a JSON document tree of blocks. Blocks are flat (no nesting
 * in v1) for simplicity; layout primitives are achieved by the Spacer
 * block and per-block padding/background controls instead of a nested
 * Row/Column system. Each block has a stable `id` so editor state
 * (selection, undo history) survives reorders.
 *
 * Variable interpolation runs over any text/markdown field at render
 * time using the existing `{{var}}` interpolator in
 * `packages/app/src/modules/scheduler/template-resolver.ts`.
 */
import { z } from 'zod';
// ============================================================================
// Block types
// ============================================================================
export const KitePageBlockTypeSchema = z.enum([
    'hero',
    'text',
    'image',
    'button',
    'form',
    'video',
    'faq',
    'spacer',
    'footer',
]);
// Per-block style controls that apply to every block type.
export const KiteBlockStyleSchema = z
    .object({
    background_color: z.string().max(32).optional(),
    text_color: z.string().max(32).optional(),
    font_size_px: z.number().int().min(8).max(96).optional(),
    padding_y_px: z.number().int().min(0).max(256).optional(),
    padding_x_px: z.number().int().min(0).max(256).optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
})
    .partial();
// ----- Per-block props --------------------------------------------------------
const HeroPropsSchema = z.object({
    headline: z.string().max(200).default(''),
    subheadline: z.string().max(400).default(''),
    background_image_url: z.string().url().max(2000).optional().or(z.literal('')),
});
const TextPropsSchema = z.object({
    /** Plain text or simple markdown — the renderer escapes HTML before interpolating. */
    body: z.string().max(20_000).default(''),
});
const ImagePropsSchema = z.object({
    url: z.string().url().max(2000).or(z.literal('')).default(''),
    alt: z.string().max(200).default(''),
    width_pct: z.number().int().min(10).max(100).default(100),
});
const ButtonPropsSchema = z.object({
    label: z.string().max(80).default('Click me'),
    href: z.string().max(2000).default('#'),
    variant: z.enum(['primary', 'secondary']).default('primary'),
});
const FormFieldSchema = z.object({
    name: z.string().regex(/^[a-z][a-z0-9_]{0,40}$/, 'snake_case 1-41 chars'),
    label: z.string().max(120),
    type: z.enum(['text', 'email', 'tel', 'textarea', 'select', 'checkbox']),
    required: z.boolean().default(false),
    options: z.array(z.string().max(120)).optional(),
    placeholder: z.string().max(200).optional(),
});
const FormPropsSchema = z.object({
    fields: z.array(FormFieldSchema).max(40).default([]),
    submit_label: z.string().max(80).default('Submit'),
    /** Sent as the `Webhook-Event` header on downstream notification, e.g. 'rsvp.submitted'. */
    event_name: z.string().max(80).default('kite.form.submitted'),
    success_message: z.string().max(400).default('Thanks! Your submission was received.'),
    /** Optional URL to redirect to on success instead of showing the success message. */
    redirect_url: z.string().url().max(2000).optional().or(z.literal('')),
    /**
     * Optional address to receive a transactional email summary on every
     * successful submission. When set, the app sends a short plain-text
     * notification via the platform transactional email service on top of
     * the webhook fan-out — useful for ops teams that don't run a webhook
     * receiver. Empty string = disabled.
     */
    notify_email: z.string().email().max(254).optional().or(z.literal('')),
});
const VideoPropsSchema = z.object({
    /** YouTube, Vimeo, or direct MP4 URL. Renderer detects provider. */
    url: z.string().url().max(2000).or(z.literal('')).default(''),
    caption: z.string().max(400).default(''),
});
const FaqPropsSchema = z.object({
    items: z
        .array(z.object({
        question: z.string().max(400),
        answer: z.string().max(4000),
    }))
        .max(40)
        .default([]),
});
const SpacerPropsSchema = z.object({
    height_px: z.number().int().min(4).max(512).default(32),
});
const FooterPropsSchema = z.object({
    text: z.string().max(2000).default(''),
    /** Optional unsubscribe URL; rendered as a small underlined link. */
    unsubscribe_url: z.string().max(2000).default(''),
});
// ----- Discriminated union ----------------------------------------------------
export const KitePageBlockSchema = z.discriminatedUnion('type', [
    z.object({ id: z.string().min(1).max(80), type: z.literal('hero'), props: HeroPropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('text'), props: TextPropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('image'), props: ImagePropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('button'), props: ButtonPropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('form'), props: FormPropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('video'), props: VideoPropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('faq'), props: FaqPropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('spacer'), props: SpacerPropsSchema, style: KiteBlockStyleSchema.optional() }),
    z.object({ id: z.string().min(1).max(80), type: z.literal('footer'), props: FooterPropsSchema, style: KiteBlockStyleSchema.optional() }),
]);
// ----- Document --------------------------------------------------------------
export const KitePageThemeSchema = z.object({
    background_color: z.string().max(32).default('#ffffff'),
    text_color: z.string().max(32).default('#0f172a'),
    accent_color: z.string().max(32).default('#2563eb'),
    font_family: z
        .enum(['system', 'serif', 'mono', 'inter', 'roboto'])
        .default('system'),
    max_width_px: z.number().int().min(320).max(1600).default(720),
});
export const KitePageDocumentSchema = z.object({
    version: z.literal(1),
    theme: KitePageThemeSchema,
    blocks: z.array(KitePageBlockSchema).max(200),
});
export const KitePageStatusSchema = z.enum(['draft', 'published']);
// ----- API surface -----------------------------------------------------------
export const KitePageSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,59}$/, 'lowercase, digits, hyphen; 1-60 chars'),
    title: z.string().min(1).max(200),
    status: KitePageStatusSchema,
    draft_document: KitePageDocumentSchema,
    published_document: KitePageDocumentSchema.nullable(),
    published_version: z.number().int().nullable(),
    /** Slug snapshot at last publish time. Public URL routes off this. */
    published_slug: z.string().nullable(),
    /** Title snapshot at last publish time. Rendered as the <title> tag. */
    published_title: z.string().nullable(),
    draft_version: z.number().int(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const KitePageCreateSchema = z.object({
    slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,59}$/),
    title: z.string().min(1).max(200),
});
export const KitePageUpdateSchema = z
    .object({
    title: z.string().min(1).max(200).optional(),
    slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,59}$/).optional(),
    draft_document: KitePageDocumentSchema.optional(),
})
    .strict();
// ----- Form submissions ------------------------------------------------------
export const KiteFormSubmissionSchema = z.object({
    id: z.string(),
    customer_id: z.string(),
    page_id: z.string(),
    page_slug: z.string(),
    /** Form block id within the page document this submission came from. */
    block_id: z.string(),
    /** Field-name → value map, validated server-side against the block's field schema. */
    values: z.record(z.string(), z.union([z.string(), z.boolean()])),
    submitted_at: z.string().datetime(),
    ip: z.string().nullable(),
    user_agent: z.string().nullable(),
});
/**
 * Build an empty starter document so the editor doesn't open to a blank
 * canvas. One Hero + one Text + one Button is enough to convey "edit me".
 */
export function emptyKitePageDocument() {
    return {
        version: 1,
        theme: {
            background_color: '#ffffff',
            text_color: '#0f172a',
            accent_color: '#2563eb',
            font_family: 'system',
            max_width_px: 720,
        },
        blocks: [
            {
                id: 'b_hero',
                type: 'hero',
                props: {
                    headline: 'Welcome, {{first_name}}!',
                    subheadline: 'Edit this page in the KITE Pages editor.',
                    background_image_url: '',
                },
            },
            {
                id: 'b_text',
                type: 'text',
                props: { body: 'Replace this with your message.' },
            },
            {
                id: 'b_button',
                type: 'button',
                props: { label: 'Confirm', href: '#', variant: 'primary' },
            },
        ],
    };
}
//# sourceMappingURL=kite-pages.js.map